import type { MailMessage } from "../../scenario/types";
import { FILES, PEOPLE, type Person, type Role, type SimFile, type World } from "../sendItEngine";
import type { FileExt } from "./icons";

export type FileId = "arrival" | "working" | "external" | "schedule" | "flyer";
export type MissionId = "m1" | "m2" | "m3" | "m4" | "m5" | "m6";

export interface Mission {
  id: MissionId;
  title: string;
  /** The request text the learner reads in Mail. The grader in sendItEngine.ts judges the outcome. */
  request: string;
  mail: {
    id: string;
    from: string;
    fromAddress: string;
    initials: string;
    subject: string;
    receivedAt: string;
    body: string;
  };
  clock: string;
  date: string;
}

export const MISSIONS: Mission[] = [
  {
    id: "m1",
    title: "Ask Morgan to read the arrival guide",
    request:
      "Please send the workshop arrival guide to Morgan Lee in our organisation. Morgan only needs to read it. This version is for Morgan only.",
    mail: {
      id: "mail-m1",
      from: "Priya Shah",
      fromAddress: "priya.shah@northstar.example",
      initials: "PS",
      subject: "Arrival guide for Morgan",
      receivedAt: "9:04 AM",
      body:
        "Hi,\n\nPlease send the workshop arrival guide to Morgan Lee in our organisation. Morgan only needs to read it. This version is for Morgan only.\n\nThanks,\nPriya",
    },
    clock: "9:04 AM",
    date: "24/06/2026",
  },
  {
    id: "m2",
    title: "Share the public flyer",
    request: "Share the approved public workshop flyer so anyone receiving the link can read it without signing in.",
    mail: {
      id: "mail-m2",
      from: "Priya Shah",
      fromAddress: "priya.shah@northstar.example",
      initials: "PS",
      subject: "Flyer for the public page",
      receivedAt: "9:21 AM",
      body:
        "Hi,\n\nShare the approved public workshop flyer so anyone receiving the link can read it without signing in.\n\nThanks,\nPriya",
    },
    clock: "9:21 AM",
    date: "24/06/2026",
  },
  {
    id: "m3",
    title: "Check what an app is allowed to do",
    request:
      "Two document tools are asking to connect. Use only the one listed in the Sharing Guide, and only for the selected workshop document.",
    mail: {
      id: "mail-m3",
      from: "IT Service Desk",
      fromAddress: "it.servicedesk@northstar.example",
      initials: "IT",
      subject: "Two document tools want to connect",
      receivedAt: "9:38 AM",
      body:
        "Hello,\n\nTwo document tools are asking to connect. Use only the one listed in the Sharing Guide, and only for the selected workshop document.\n\nBoth requests are waiting in Connected apps.\n\nIT Service Desk",
    },
    clock: "9:38 AM",
    date: "24/06/2026",
  },
  {
    id: "m4",
    title: "Ask the external facilitator to review the guide",
    request:
      "Alex Kim, our approved external facilitator, needs to suggest changes to the workshop guide. Use the version approved for external review. Access is needed until the workshop ends.",
    mail: {
      id: "mail-m4",
      from: "Priya Shah",
      fromAddress: "priya.shah@northstar.example",
      initials: "PS",
      subject: "Alex needs to review the guide",
      receivedAt: "10:02 AM",
      body:
        "Hi,\n\nAlex Kim, our approved external facilitator, needs to suggest changes to the workshop guide. Use the version approved for external review. Access is needed until the workshop ends.\n\nThanks,\nPriya",
    },
    clock: "10:02 AM",
    date: "24/06/2026",
  },
  {
    id: "m5",
    title: "Let the project team update the schedule",
    request: "Morgan and Riley need to update the workshop schedule together. Keep it within this project team.",
    mail: {
      id: "mail-m5",
      from: "Priya Shah",
      fromAddress: "priya.shah@northstar.example",
      initials: "PS",
      subject: "Schedule: Morgan and Riley",
      receivedAt: "10:18 AM",
      body:
        "Hi,\n\nMorgan and Riley need to update the workshop schedule together. Keep it within this project team.\n\nThanks,\nPriya",
    },
    clock: "10:18 AM",
    date: "24/06/2026",
  },
  {
    id: "m6",
    title: "Close the facilitator's access",
    request: "The external facilitator's work is complete. Remove access they no longer need. Keep the team's required records.",
    mail: {
      id: "mail-m6",
      from: "Priya Shah",
      fromAddress: "priya.shah@northstar.example",
      initials: "PS",
      subject: "Workshop finished: close Alex's access",
      receivedAt: "5:30 PM",
      body:
        "Hi,\n\nThe workshop has finished. The external facilitator's work is complete. Remove access they no longer need. Keep the team's required records.\n\nThanks,\nPriya",
    },
    clock: "5:30 PM",
    date: "26/06/2026",
  },
];

/** Older, unrelated mail that is already in the inbox. */
export const FILLER_MAILS: MailMessage[] = [
  {
    id: "mail-f1",
    from: "Facilities",
    fromAddress: "facilities@northstar.example",
    to: "you",
    subject: "Room Birch is booked for the workshop",
    preview: "Birch on level 2 is confirmed for both days.",
    receivedAt: "Mon",
    initials: "FA",
    body: "Hello,\n\nRoom Birch on level 2 is confirmed for both workshop days. Chairs will be set in a U shape.\n\nFacilities",
  },
  {
    id: "mail-f2",
    from: "People Team",
    fromAddress: "people@northstar.example",
    to: "you",
    subject: "Please update your out-of-office",
    preview: "A quick reminder before the end of the week.",
    receivedAt: "Mon",
    initials: "PT",
    body: "Hi,\n\nA quick reminder to update your out-of-office message before the end of the week.\n\nPeople Team",
  },
];

export const FILE_ORDER: FileId[] = ["arrival", "working", "external", "schedule", "flyer"];

export const FILE_META: Record<FileId, { ext: FileExt; type: string; modified: string; short: string }> = {
  arrival: { ext: "docx", type: "Word document", modified: "Mon 14:10", short: "Arrival guide" },
  working: { ext: "docx", type: "Word document", modified: "Mon 15:42", short: "Working copy" },
  external: { ext: "docx", type: "Word document", modified: "Tue 09:05", short: "External guide" },
  schedule: { ext: "xlsx", type: "Excel worksheet", modified: "Tue 11:30", short: "Schedule" },
  flyer: { ext: "pdf", type: "PDF document", modified: "Tue 16:20", short: "Flyer" },
};

export const ROLE_LABEL: Record<Role, string> = { view: "View", comment: "Comment", edit: "Edit" };
export const ROLE_TEXT: Record<Role, string> = {
  view: "Can read the file. Cannot change it.",
  comment: "Can read and leave feedback. Cannot change the file.",
  edit: "Can read, comment and change the file.",
};

export const TEAM_ID = "project-team";

export function fileOf(id: string): SimFile {
  return FILES.find((f) => f.id === id) ?? FILES[0];
}

export function personOf(id: string): Person | undefined {
  return PEOPLE.find((p) => p.id === id);
}

export function placeOf(world: World, fileId: string): "project" | "shared-folder" {
  return world.filePlace[fileId] ?? "project";
}

export function placeLabel(world: World, fileId: string): string {
  return placeOf(world, fileId) === "project" ? "Workshop project (restricted)" : "Workshop archive (shared folder)";
}

/** The short form used in the Explorer Location column. */
export function placeShort(world: World, fileId: string): string {
  return placeOf(world, fileId) === "project" ? "Project (restricted)" : "Archive (shared folder)";
}

export interface AppRequest {
  id: "workshop-helper" | "broad-viewer";
  name: string;
  publisher: string;
  blurb: string;
  scope: "selected-doc" | "broad";
  permissions: string[];
  tint: string;
}

export const APP_REQUESTS: AppRequest[] = [
  {
    id: "workshop-helper",
    name: "Workshop Helper",
    publisher: "Workshop Tools Ltd",
    blurb: "Opens a document so people can work on it together.",
    scope: "selected-doc",
    permissions: ["Open the selected workshop document"],
    tint: "#2f7d6d",
  },
  {
    id: "broad-viewer",
    name: "Quick Viewer",
    publisher: "Quick Viewer Online",
    blurb: "Fast, free, one-click viewing for all your documents.",
    scope: "broad",
    permissions: ["See all of your files", "Read your mailbox", "Send mail as you"],
    tint: "#a8672a",
  },
];
